import type { NavigationNode } from '../../types/link';
import { ref } from 'vue';
import { dataFetch } from '../helpers/dataFetch';

const navData = ref<NavigationNode[]>();

export const getNav = async (
  caasUrl: string,
  navigationServiceUrl: string,
  project: string,
  instance: string,
  type: 'primary' | 'meta' | 'secondary' | 'social',
  stage: string,
  language: string,
  locale: string,
  prefix: string,
  storeId: string,
) => {
  const localeLowerCase = (locale || language)?.toLowerCase();

  const storedData = sessionStorage.getItem(storeId);
  const eTagLocalStore = `${storeId}-eTag`;
  const eTagExisting = sessionStorage.getItem(eTagLocalStore);

  const url = new URL(navigationServiceUrl, caasUrl)
    .toString()
    .replace('{#project}', project)
    .replace('{#instance}', instance)
    .replace('{#types}', type)
    .replace('{#stage}', stage)
    .replace('{#locale}', localeLowerCase)
    .replace('{#prefix}', prefix || '');

  try {
    if (eTagExisting) {
      const response = await fetch(url, {
        headers: {
          'If-None-Match': eTagExisting,
        },
      });
      if (response.status === 304) {
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          navData.value = parsedData.items satisfies NavigationNode[];
        } else {
          const { data, eTag } = await dataFetch(url);
          navData.value = data;

          if (navData.value) {
            sessionStorage.setItem(storeId, JSON.stringify({ items: navData.value }));
          }

          console.debug(
            `${navigationServiceUrl} >>> eTag unchanged: ${eTag}, data fetched due to lack of SessionStorage entry`,
            navData.value,
          );
        }
      } else {
        if (!response.ok) {
          throw new Error(`${navigationServiceUrl} >>> error, ${response.status}`);
        }

        const eTag = response.headers.get('Etag');

        if (eTag) {
          sessionStorage.setItem(eTagLocalStore, eTag);
        }

        navData.value = await response.json();
        if (navData.value && Object.keys(navData.value).length) {
          sessionStorage.setItem(
            storeId,
            JSON.stringify({
              items: navData.value,
            }),
          );
        }
        console.debug(`${navigationServiceUrl} >>> eTag changed, data initialized`, navData.value);
      }
    } else {
      const { data, eTag } = await dataFetch(url);
      if (eTag) {
        sessionStorage.setItem(eTagLocalStore, eTag);
      }
      navData.value = data;
      sessionStorage.setItem(
        storeId,
        JSON.stringify({
          items: navData.value,
        }),
      );

      console.debug(`${navigationServiceUrl} >>> first eTag, data initialized`, navData.value);
    }

    return navData.value;
  } catch (error) {
    console.error(`${navigationServiceUrl} >>> error`, error);
  }
};
